import type {Icon} from '@primer/octicons-react'
import {
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
} from '@primer/octicons-react'
import type {CatchUpTimelineSummary} from '@github-ui/dotcom-schema/types/pull_requests/v1/activity'
import {useSuspenseActivityPageData} from '../../data/use-activity-page-data'
import {SidebarActivityRow} from './SidebarActivityRow'

export const STATE_TYPE_MAP: Record<string, string> = {
  closed: 'Closed',
  reopened: 'Reopened',
  'marked-as-draft': 'Marked as draft',
  'ready-for-review': 'Marked ready for review',
  merged: 'Merged',
}

const STATE_ICON_MAP: Record<string, Icon> = {
  closed: GitPullRequestClosedIcon,
  reopened: GitPullRequestIcon,
  'marked-as-draft': GitPullRequestDraftIcon,
  'ready-for-review': GitPullRequestIcon,
  merged: GitMergeIcon,
}

function getLastStateType(summary?: CatchUpTimelineSummary): string | null {
  const all: Array<{type: string; timestamp: string}> = [
    ...(summary?.closed?.events ?? []).map(e => ({type: 'closed', timestamp: e.timestamp})),
    ...(summary?.reopened?.events ?? []).map(e => ({type: 'reopened', timestamp: e.timestamp})),
    ...(summary?.markedAsDraft?.events ?? []).map(e => ({type: 'marked-as-draft', timestamp: e.timestamp})),
    ...(summary?.readyForReview?.events ?? []).map(e => ({type: 'ready-for-review', timestamp: e.timestamp})),
    ...(summary?.merged?.events ?? []).map(e => ({type: 'merged', timestamp: e.timestamp})),
  ]
  if (all.length === 0) return null
  all.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  return all.at(-1)?.type ?? null
}

export function StateRow() {
  const {summary} = useSuspenseActivityPageData()
  const lastType = getLastStateType(summary)
  if (!lastType) return null

  const label = STATE_TYPE_MAP[lastType] ?? lastType
  const icon = STATE_ICON_MAP[lastType] ?? GitMergeIcon
  const expectedCount =
    (summary?.closed?.count ?? 0) +
    (summary?.reopened?.count ?? 0) +
    (summary?.markedAsDraft?.count ?? 0) +
    (summary?.readyForReview?.count ?? 0) +
    (summary?.merged?.count ?? 0)

  return <SidebarActivityRow icon={icon} label={label} group="state" expectedCount={expectedCount} />
}
